import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { TFunction } from 'i18next';
import { getBrandingColor, getBrandingColorRgba } from '../../utils/brandingStyles';

export interface NotificationMessage {
    id: string;
    type: 'insight' | 'update' | 'event' | 'ad';
    label: string;
    text: string;
    color: string;
    bg: string;
    border: string;
}

interface ScrollingNotificationProps {
    messages: NotificationMessage[];
    autoRotateInterval?: number;
    onDismiss?: (messageId: string) => void;
    onClick?: (message: NotificationMessage) => void;
    /** Classe sur le conteneur racine (ex. rail interview) */
    className?: string;
}

export const ScrollingNotification: React.FC<ScrollingNotificationProps> = ({
    messages,
    autoRotateInterval = 7000,
    onDismiss,
    onClick,
    className,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);

    const currentMessage = messages[currentIndex];

    const nextMessage = () => {
        setIsVisible(false);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % messages.length);
            setIsVisible(true);
        }, 200);
    };

    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDismiss) {
            onDismiss(currentMessage.id);
        }
        nextMessage();
    };

    const handleClick = () => {
        if (onClick) {
            onClick(currentMessage);
        }
        nextMessage();
    };

    useEffect(() => {
        if (messages.length <= 1) return;

        const interval = setInterval(() => {
            nextMessage();
        }, autoRotateInterval);

        return () => clearInterval(interval);
    }, [currentIndex, autoRotateInterval, messages.length]);

    if (!currentMessage || messages.length === 0) return null;

    const rootClass = ['scrolling-notification-root', className].filter(Boolean).join(' ');

    return (
        <div className={rootClass} style={{ marginTop: 0, padding: 0, borderTop: 'none' }}>
            <div
                style={{
                    borderRadius: '8px',
                    padding: '14px 13px',
                    minHeight: '112px',
                    transition: 'opacity 0.35s',
                    cursor: 'pointer',
                    position: 'relative',
                    background: currentMessage.bg,
                    border: `1px solid ${currentMessage.border}`,
                    opacity: isVisible ? 1 : 0,
                    boxSizing: 'border-box',
                }}
                onClick={handleClick}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '8px'
                }}>
                    <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.55px',
                        color: currentMessage.color
                    }}>
                        {currentMessage.label}
                    </span>
                    <span
                        style={{
                            fontSize: '11px',
                            color: 'var(--text-tertiary)',
                            cursor: 'pointer',
                            lineHeight: 1,
                            padding: '2px'
                        }}
                        onClick={handleDismiss}
                        title="Dismiss"
                    >
                        <X size={12} strokeWidth={2} />
                    </span>
                </div>
                <p style={{
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    lineHeight: 1.55,
                    margin: 0
                }}>
                    {currentMessage.text}
                </p>
            </div>

            {/* Dot indicators */}
            {messages.length > 1 && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '4px',
                    marginTop: '7px'
                }}>
                    {messages.map((_, index) => (
                        <div
                            key={index}
                            style={{
                                width: index === currentIndex ? '14px' : '4px',
                                height: '4px',
                                borderRadius: '2px',
                                background: index === currentIndex 
                                    ? currentMessage.color 
                                    : 'rgba(255,255,255,0.12)',
                                transition: 'all 0.3s'
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/** Messages du rail droit Interview — alignés sur la langue i18n */
export function buildInterviewRailNotificationMessages(t: TFunction): NotificationMessage[] {
    return [
        {
            id: 'insight-1',
            type: 'insight',
            color: getBrandingColor(),
            bg: getBrandingColorRgba(0.08),
            border: getBrandingColorRgba(0.18),
            label: t('views.processInterview.interviewRailNotifications.insight.label'),
            text: t('views.processInterview.interviewRailNotifications.insight.text'),
        },
        {
            id: 'update-1',
            type: 'update',
            color: '#4A9EE8',
            bg: 'rgba(74,158,232,0.08)',
            border: 'rgba(74,158,232,0.18)',
            label: t('views.processInterview.interviewRailNotifications.update.label'),
            text: t('views.processInterview.interviewRailNotifications.update.text'),
        },
        {
            id: 'event-1',
            type: 'event',
            color: '#E8A73A',
            bg: 'rgba(232,167,58,0.08)',
            border: 'rgba(232,167,58,0.18)',
            label: t('views.processInterview.interviewRailNotifications.event.label'),
            text: t('views.processInterview.interviewRailNotifications.event.text'),
        },
        {
            id: 'ad-1',
            type: 'ad',
            color: '#8A8880',
            bg: 'rgba(255,255,255,0.04)',
            border: 'rgba(255,255,255,0.10)',
            label: t('views.processInterview.interviewRailNotifications.ad.label'),
            text: t('views.processInterview.interviewRailNotifications.ad.text'),
        },
    ];
}

/** @deprecated Utiliser buildInterviewRailNotificationMessages(t) pour respecter la langue UI */
export const defaultNotificationMessages: NotificationMessage[] = [
    {
        id: 'insight-1',
        type: 'insight',
        color: getBrandingColor(),
        bg: getBrandingColorRgba(0.08),
        border: getBrandingColorRgba(0.18),
        label: 'Insight',
        text: 'Chevauchement de rôles détecté entre Fournisseur et Consommateur — peut indiquer un écart de transfert.',
    },
    {
        id: 'update-1',
        type: 'update',
        color: '#4A9EE8',
        bg: 'rgba(74,158,232,0.08)',
        border: 'rgba(74,158,232,0.18)',
        label: 'Mise à jour plateforme',
        text: "Analyse & Optimisation est maintenant disponible. Lancez votre première analyse d'écart sur ce processus.",
    },
    {
        id: 'event-1',
        type: 'event',
        color: '#E8A73A',
        bg: 'rgba(232,167,58,0.08)',
        border: 'rgba(232,167,58,0.18)',
        label: 'Webinaire',
        text: 'Lean Six Sigma & IA — 10 avril à 14h. Inscrivez-vous pour réserver votre place.',
    },
    {
        id: 'ad-1',
        type: 'ad',
        color: '#8A8880',
        bg: 'rgba(255,255,255,0.04)',
        border: 'rgba(255,255,255,0.10)',
        label: 'Upgrade',
        text: 'Débloquez les versions illimitées de diagrammes avec le plan Business.',
    },
];
